import { globalWindow } from 'global/global';
import * as adminApi from 'api/adminApi';
import {
  loadedAdminInit, loadedUserStats, setAdminSession,
} from 'reducers/admin/account';
import { loadedAdminGroups } from 'reducers/admin/groups';
import { loadedAdminUsers } from 'reducers/admin/users';
import { loadedCampaigns } from 'reducers/admin/campaigns';
import { loadedFormsAndLists, loadedHierarchies } from 'reducers/admin/meta';

/**
 *  Replaces initUserGroups / initCampaigns / initLocationHierarchy / initAdmin
 *  from the Backbone bootstrap (WebContent/assets/admin/js/admin.js).
 */

const asNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 *  The few session values admin.tmpl.jsp still writes onto window. Everything
 *  else about the account now arrives from /json/admin/init, which overwrites
 *  these as soon as it resolves.
 */
export const readAdminSession = () => {
  const w = globalWindow as unknown as Record<string, unknown>;
  return setAdminSession({
    groupId: asNumber(w.groupId) || null,
    userId: asNumber(w.userID ?? w.userId) || null,
    /** Origin of the Activity view's period columns; not part of /init. */
    logDate: typeof w.log_date === 'string' ? w.log_date : null,
    db: typeof w.db === 'string' ? w.db : null,
  });
};

export const loadUserStats = () => {
  return async (dispatch) => {
    try {
      dispatch(loadedUserStats(await adminApi.fetchUserStats()));
    } catch (e) {
      console.log(e);
    }
  };
};

/** Loads everything the admin tool needs, in parallel. */
export const loadAdminData = () => {
  return async (dispatch) => {
    dispatch(readAdminSession());

    /**
     *  Loaded in parallel, each independently — one failing endpoint must not
     *  leave the whole tool empty. (Promise.allSettled is not available under
     *  the project's es7 lib target.)
     */
    const settled = <T>(p: Promise<T>): Promise<T | null> =>
      p.then((value) => value).catch((e) => {
        console.log(e);
        return null;
      });

    const [userGroups, campaigns, hierarchies, init, stats] = await Promise.all([
      settled(adminApi.fetchUserGroups()),
      settled(adminApi.fetchCampaigns()),
      settled(adminApi.fetchHierarchies()),
      settled(adminApi.fetchAdminInit()),
      settled(adminApi.fetchUserStats()),
    ]);

    if (userGroups) {
      const { groupList, userList, formLists, forms, db } = userGroups;
      dispatch(loadedAdminGroups(groupList || []));
      dispatch(loadedAdminUsers(userList || []));
      dispatch(loadedFormsAndLists({ formLists: formLists || [], forms: forms || [] }));
      dispatch(setAdminSession({ db }));
    }
    if (campaigns) {
      dispatch(loadedCampaigns(campaigns));
    }
    if (hierarchies) {
      dispatch(loadedHierarchies(hierarchies));
    }
    if (init) {
      dispatch(loadedAdminInit(init));
    }
    if (stats) {
      dispatch(loadedUserStats(stats));
    }
  };
};
